import { useState, useEffect, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Paper,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  IconButton,
  Snackbar,
  InputAdornment,
  Pagination,
  Stack
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import { useAuth } from '../../../components/auth/AuthContext';
import AdminTabs from '../../../components/admin/AdminTabs';
import AddEditHotelDialog from './AddEditHotelDialog';
import { Hotel } from '../../../types/hotel';
import { APIHOST } from '../../../common';

function HotelsPageContent() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [hotels, setHotels] = useState<Hotel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [showDialog, setShowDialog] = useState(false);
  const [editingHotel, setEditingHotel] = useState<Hotel | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  const isAuthenticated = currentUser && (currentUser.userType === 'internal' || currentUser.userType === 'admin');

  useEffect(() => {
    if (isAuthenticated) {
      fetchHotels(1, searchQuery);
    } else {
      navigate('/admin');
    }
  }, [navigate, isAuthenticated]);

  const fetchHotels = async (page = currentPage, search = searchQuery) => {
    try {
      setLoading(true);
      setError('');

      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      // Build query parameters
      const params = new URLSearchParams({
        'sort': 'longName:asc',
        'pagination[page]': page.toString(),
        'pagination[pageSize]': pageSize.toString()
      });

      // Add search filter if query exists
      if (search.trim()) {
        params.append('filters[$or][0][name][$containsi]', search.trim());
        params.append('filters[$or][1][longName][$containsi]', search.trim());
        params.append('filters[$or][2][addressCity][$containsi]', search.trim());
        params.append('filters[$or][3][addressState][$containsi]', search.trim());
      }

      const response = await fetch(
        `${APIHOST}/api/hotels?${params.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch hotels');
      }

      const data = await response.json();
      setHotels(data.data || []);
      setTotalCount(data.meta?.pagination?.total || 0);
      setTotalPages(data.meta?.pagination?.pageCount || 0);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load hotels');
      console.error('Hotels error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddHotel = () => {
    setEditingHotel(null);
    setShowDialog(true);
  };

  const handleEditHotel = (hotel: Hotel) => {
    setEditingHotel(hotel);
    setShowDialog(true);
  };

  const handleDeleteHotel = async (hotel: Hotel) => {
    if (!window.confirm(`Are you sure you want to delete "${hotel.longName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`${APIHOST}/api/hotels/${hotel.documentId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete hotel');
      }

      setSnackbarMessage('Hotel deleted successfully');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);

      await fetchHotels(currentPage, searchQuery);

    } catch (err) {
      console.error('Error deleting hotel:', err);
      setSnackbarMessage('Failed to delete hotel. Please try again.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleDialogSuccess = async (isEdit: boolean) => {
    setSnackbarMessage(isEdit ? 'Hotel updated successfully' : 'Hotel created successfully');
    setSnackbarSeverity('success');
    setSnackbarOpen(true);
    await fetchHotels(currentPage, searchQuery);
  };

  const handleCloseDialog = () => {
    setShowDialog(false);
    setEditingHotel(null);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const query = event.target.value;
    setSearchQuery(query);
    setCurrentPage(1); // Reset to first page when searching
    
    // Debounce search - wait 500ms after user stops typing
    const timeoutId = setTimeout(() => {
      fetchHotels(1, query);
    }, 500);

    return () => clearTimeout(timeoutId);
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
    fetchHotels(page, searchQuery);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setCurrentPage(1);
    fetchHotels(1, '');
  };

  if (!isAuthenticated) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Alert severity="warning">
          Please log in to access this page.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', py: 4 }}>
    

        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6">
              Hotels {totalCount > 0 && `(${totalCount} total)`}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={handleAddHotel}
            >
              Add Hotel
            </Button>
          </Box>

          {/* Search Bar */}
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              placeholder="Search hotels by name, city, or state..."
              value={searchQuery}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <Button
                      size="small"
                      onClick={handleClearSearch}
                      sx={{ minWidth: 'auto', p: 0.5 }}
                    >
                      Clear
                    </Button>
                  </InputAdornment>
                )
              }}
            />
          </Box>

          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
              <Typography variant="body2" sx={{ ml: 2 }}>
                Loading hotels...
              </Typography>
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {!loading && hotels.length === 0 && !error && (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
              {searchQuery 
                ? `No hotels found matching "${searchQuery}". Try adjusting your search terms.`
                : 'No hotels found. Add your first hotel to get started.'
              }
            </Typography>
          )}

          {!loading && hotels.length > 0 && searchQuery && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Found {totalCount} hotel{totalCount !== 1 ? 's' : ''} matching "{searchQuery}"
            </Alert>
          )}

          {!loading && hotels.length > 0 && (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Hotel Name</strong></TableCell>
                    <TableCell><strong>Location</strong></TableCell>
                    <TableCell><strong>Website</strong></TableCell>
                    <TableCell align="center"><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {hotels.map((hotel) => (
                    <TableRow key={hotel.documentId} hover>
                      <TableCell>
                        <Box>
                          <Typography variant="body1" fontWeight="medium">
                            {hotel.longName}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {hotel.name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {[hotel.addressStreet1, hotel.addressStreet2].filter(Boolean).join(', ')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {[hotel.addressCity, hotel.addressState, hotel.addressZip]
                            .filter(Boolean)
                            .join(', ') || 'No address provided'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {hotel.website ? (
                          <Button
                            variant="text"
                            size="small"
                            href={hotel.website}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Visit Website
                          </Button>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No website
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          <IconButton
                            size="small"
                            onClick={() => handleEditHotel(hotel)}
                            color="primary"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteHotel(hotel)}
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Pagination */}
          {!loading && totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Stack spacing={2} alignItems="center">
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={handlePageChange}
                  color="primary"
                  size="medium"
                  showFirstButton
                  showLastButton
                />
                <Typography variant="body2" color="text.secondary">
                  Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} hotels
                </Typography>
              </Stack>
            </Box>
          )}
        </Paper>

        {/* Add/Edit Hotel Dialog */}
        <AddEditHotelDialog
          open={showDialog}
          onClose={handleCloseDialog}
          editingHotel={editingHotel}
          onSuccess={() => handleDialogSuccess(!!editingHotel)}
        />

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={4000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert 
            onClose={handleSnackbarClose} 
            severity={snackbarSeverity}
            sx={{ width: '100%' }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
   
    </Box>
  );
}

export default function HotelsPage() {
  return (
    <Suspense fallback={
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    }>
      <HotelsPageContent />
    </Suspense>
  );
}